﻿import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { Breadcrumbs } from 'components/navigation';
import { Button } from "components/buttons";
import { ListingTable } from "components/listing";
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { UserService } from 'services';
import { CheckService } from 'services/CheckService';
import { ShowHide } from "components/layout";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Filter, FilterButton } from 'components/filtering';
import { useDispatch } from 'react-redux';
import { SetRouting, SetFiltering } from 'state/actions';
import { FormTextInput, FormLocationSelector, FormToggleList } from "components/form-fields";
import { DateToLocal } from "components/dates";
import { NotificationToaster } from 'components/notifications';
import { Intent } from '@blueprintjs/core';
import { useDebounce } from 'hooks/useDebounce';
import axios from 'axios';
import { getEntityLocation } from 'pages/checks/utils';

export function ChecksListing(
    {
        withBreadcrumbs = true,
        withCheckSettingsButton = true,
        withManageTemplatesButton = true,
        withLocationsFilter = true,
        category = '',
        breadcrumbs,
        employeeId,
        entityId,
        id,
        route
    }
) {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;
    const categoryName = "category";
    const subCategoryName = "subcategory";

    const [isInitalSetupComplete, setInitalSetupComplete] = useState(false);
    const dispatch = useDispatch();

    const [tableSortBy, setTableSortBy] = useState({ headerName: "Date Completed", sortName: "enddatetime" });
    const [tableSortDir, setTableSortDir] = useState("D");

    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const inputRef = useRef(null);

    const [tableRows, setTableRows] = useState([]);
    const [isLoadingData, setLoadingData] = useState(false);

    const [filterSetupComplete, setFilterSetupComplete] = useState(false);
    const [pageFilters, setPageFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [predefinedFilters, setPredefinedFilters] = useState([]);
    const [predefinedFiltersSetupComplete, setPredefinedFiltersSetupComplete] = useState(false);

    const [isTemplateOptionsLoaded, setTemplateOptionsLoaded] = useState(false);
    const [templateOptions, setTemplateOptions] = useState(null);

    const [isTemplatesFilterOptionsLoaded, setTemplatesFilterOptionsLoaded] = useState(false);
    const [templatesFilterOptions, setTemplatesFilterOptions] = useState(null);

    const [useHierarchy, setUseHierarchy] = useState(true);
    const [locationList, setLocationList] = useState({ location: [], hierarchy: [] });
    const [locationsLoading, setLocationsLoading] = useState(true);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);

    const [statuses] = useState([
        { id: "passfail", key: "passfailresult.passed", name: "Check Status Fail", count: 0 },
        { id: "requiremanagersignoff", key: "requiremanagersignoff", name: "Pending Sign-Off", count: 0 }
    ]);
    const [selectedStatus, setSelectedStatus] = useState('');

    /// <summary>
    /// This is the list of headers that will be displayed in the table.
    ///     The headers are defined in the order they will be displayed.
    /// The headerName is the text that will be displayed in the table.
    /// The sortName is the name of the field that will be used to sort the table.
    /// The onlyForFilter is an array of filters which will display this header.
    ///     Header will be displayed if array is empty.
    ///     Header will be hidden if no filter is chosen.
    ///     example: onlyForFilter: [{ filter: "subcategory", values: ["AssetGeneric"] }]
    /// The excludeForFilters is an array of filters which will hide this header.
    ///     Header will be displayed if array is empty.
    ///     Header will be hidden if no filter is chosen.
    ///     example: excludeForFilters: [{ filter: "subcategory", values: ["WalkaroundCheck"] }]
    /// </summary>
    const tableHeaders = [
        { headerName: "Check ID", sortName: "friendlyId", onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Check name", sortName: "templatename", onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Subject", sortName: "entity.registration", onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Category", sortName: categoryName, onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Sub-category", sortName: subCategoryName, onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Location", sortName: "entity.locationname", onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Completed by", sortName: "createuser.username", onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Date Completed", sortName: "enddatetime", onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Sign-off Status", sortName: "requiremanagersignoff", onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Check Status", sortName: "passfailresult.passed", onlyForFilter: [], excludeForFilters: [] },
        { headerName: "Defect Count", sortName: "defectcount", onlyForFilter: [{ filter: subCategoryName, values: ["WalkaroundCheck"] }], excludeForFilters: [] }
    ];

    const [filteredTableHeaders, setFilteredTableHeaders] = useState([]);
    const [filteredSortableHeaders, setFilteredSortableHeaders] = useState([]);

    const [noDataMessage, setNoDataMessage] = useState("No checks found, please try a different search and filter.");

    const requiredAction = "Checks:View";
    const templateBuilderAction = "Checks:TemplateBuilder";
    const templateManagerAction = "Checks:Manage";
    const canAccessPage = UserHasAccess(requiredAction);

    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentSearchTerm = useRef();
    const currentLocations = useRef();
    const currentStatus = useRef();
    var navigate = useNavigate();

    useEffect(() => {
        axios.all([
            CheckService.getTemplateOptions(),
            CheckService.getTemplatesFilterOptions(),
            UserService.getLoggedInUserLocationsForMultipleBusinessAreas([1, 2, 3, 4], true, true)
        ]).then(axios.spread(function (templateOptionsResponse, templatesFilterOptionsResponse, locationsResponse) {
            setTemplateOptions(templateOptionsResponse);
            setTemplatesFilterOptions(templatesFilterOptionsResponse);
            setLocationList(locationsResponse);
            setSelectedLocations(locationsResponse.location[0].items.map((i) => i.id));
        })).catch(() => {
            NotificationToaster.show(Intent.DANGER, 'Failed to load page, please try again.');
        });
    }, []);

    useEffect(() => {
        let filters = [];
        if (entityId != null) {
            filters.push({ name: "entity.id", value: [entityId] });       
        }
        if (employeeId != null) {
            filters.push({ name: "createuser.employeeid", value: [employeeId] });
        }

        if (category) {
            filters.push({ name: categoryName, displayName: category, value: [category] });
        }

        setPredefinedFilters(filters);
        setPredefinedFiltersSetupComplete(true);
    }, []);
    
    useEffect(() => {
        if (selectedLocations.length > 0) {
            setLocationsLoading(false);
        }
    }, [selectedLocations]);

    useEffect(() => {
        if (templateOptions !== null) {
            setTemplateOptionsLoaded(true);
        }
    }, [templateOptions]);

    useEffect(() => {
        if (templatesFilterOptions !== null) {
            setTemplatesFilterOptionsLoaded(true);
        }
    }, [templatesFilterOptions]);

    useEffect(() => {
        if (isTemplateOptionsLoaded && isTemplatesFilterOptionsLoaded && predefinedFiltersSetupComplete) {
            onFilterChange(pageFilters);
        }
        setFilterSetupComplete(isTemplateOptionsLoaded && isTemplatesFilterOptionsLoaded && predefinedFiltersSetupComplete);
    }, [isTemplateOptionsLoaded, isTemplatesFilterOptionsLoaded, predefinedFiltersSetupComplete]);

    useEffect(() => {
        setInitalSetupComplete(!locationsLoading && filterSetupComplete);
    }, [locationsLoading, filterSetupComplete]);

    useEffect(() => {
        setPageNumber(1);
        setNoDataMessage("No results found, please try a different search term.");
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (currentPageNumber.current === pageNumber &&
            currentPageSize.current === pageSize &&
            currentTableSortBy.current === tableSortBy &&
            currentTableSortDir.current === tableSortDir &&
            currentAppliedFilters.current === appliedFilters &&
            currentSearchTerm.current === debouncedSearchTerm &&
            currentLocations.current === selectedLocations &&
            currentStatus.current === selectedStatus
        ) {
            return;
        }

        searchChecks();
    }, [debouncedSearchTerm, selectedLocations, locationSelectorOpen, appliedFilters, pageSize, pageNumber, tableSortBy, tableSortDir, isInitalSetupComplete, selectedStatus]);

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
    }

    function onLocationListOpened() {
        setLocationSelectorOpen(true);
    }

    function onLocationListClose() {
        setLocationSelectorOpen(false);
    }

    function onFilterChange(listingFilters) {
        setPageNumber(1);
        setNoDataMessage("No results found, please try a different filter.");

        listingFilters = updatePageFilters(listingFilters);
        setAppliedFilters(listingFilters);
        listingFilters.forEach(element => {
            dispatch(SetFiltering(element.name, element.value));
        });
        
        const filteredHeaders = filterTableHeaders(listingFilters);
        setFilteredTableHeaders(filteredHeaders.map(header => header.headerName));
        setFilteredSortableHeaders(
            filteredHeaders
            .filter(header => header.sortName !== undefined)
            .map(header => ({ headerName: header.headerName, sortName: header.sortName }))
        );
    }

    function updatePageFilters(listingFilters) {
        let filters = [];

        if (templateOptions !== null) {
            let categories = templateOptions.templateCategories;
            if (!predefinedFilters.some((filter) => {return filter.name === categoryName;})) {
                filters = [
                    ...filters,
                    {
                        name: categoryName,
                        displayName: "Category",
                        items: categories.map((categoryValue) => {return {id: categoryValue.key, name: categoryValue.name};})
                    }
                ];
            }

            if (listingFilters !== undefined && listingFilters !== null && listingFilters.length > 0) {
                let selectedCategories = [
                    ...listingFilters.filter((filter) => {return filter.name === categoryName}),
                    ...predefinedFilters.filter((filter) => {return filter.name === categoryName;})
                ];
                
                if (selectedCategories.length > 0) {
                    categories = categories.filter((categoryValue) => {
                        return selectedCategories.some((cat) => {
                            return cat.value === categoryValue.key
                                || (Array.isArray(cat.value) && cat.value.some((catKey) => {return catKey === categoryValue.key;}));
                        });
                    });
                }
            }

            filters = [
                ...filters,
                {
                    name: subCategoryName,
                    displayName: "Sub-category",
                    items: categories
                        .filter((categoryValue) => !category || categoryValue.key === category)
                        .flatMap((categoryValue) => categoryValue.subCategories.map((subCategory) => {return { id: subCategory.key, name: subCategory.name };}))
                }
            ];
        }

        if (templatesFilterOptions !== null) {
            let templates = templatesFilterOptions.templates;
            
            if (listingFilters !== undefined && listingFilters !== null && listingFilters.length > 0) {
                let selectedSubCategories = listingFilters.filter((filter) => {return filter.name === subCategoryName});
                if (selectedSubCategories !== undefined && selectedSubCategories !== null && selectedSubCategories.length > 0) {
                    templates = templates.filter((template) => {
                        return selectedSubCategories.some((subCategory) => {
                            return subCategory.value === template.subCategory
                                || (Array.isArray(subCategory.value) && subCategory.value.some((subCatKey) => {return subCatKey === template.subCategory}));
                        })
                    });
                }
            }

            filters = [
                ...filters,
                {
                    name: "templateid",
                    displayName: "Check Template",
                    items: templates.map((template) => {return {id: template.templateId, name: template.templateName};})
                }
            ];
        }

        setPageFilters(filters);
        return filterListingFilters(listingFilters, filters);
    }

    function filterListingFilters(listingFilters, newPageFilters) {
        if (listingFilters === undefined || listingFilters === null || listingFilters.length === 0) {
            return listingFilters;
        }

        listingFilters.forEach(fltr => {
            if (fltr.value === null || fltr.value === undefined) {
                fltr.value = [];
                return;
            }

            let pageFilter = newPageFilters.find((pf) => {return pf.name === fltr.name;});
            if (pageFilter === undefined || pageFilter === null) {
                fltr.value = [];
                return;
            }

            if (Array.isArray(fltr.value) === false) {
                fltr.value = [fltr.value];
            }
            
            fltr.value = fltr.value.filter((value) => {return pageFilter.items.some((item) => {return item.id === value;});});
        });

        return listingFilters.filter((filter) => {return filter.value.length > 0;});
    }

    function filterTableHeaders(listingFilters) {
        let filters = [
            ...listingFilters,
            ...predefinedFilters
        ]
        return tableHeaders
            .filter(header => {
                const onlyForFilterMatch = header.onlyForFilter.every(filterObj => {
                    const appliedFilterObjects = filters.filter(f => f.name === filterObj.filter);
                    return appliedFilterObjects.length > 0 && appliedFilterObjects.every(appliedFilter =>
                        appliedFilter.value.every(val => filterObj.values.includes(val))
                    );
                });

                const excludeForFiltersMatch = header.excludeForFilters.some(filterObj => {
                    const appliedFilterObjects = filters.filter(f => f.name === filterObj.filter);
                    return appliedFilterObjects.length === 0 || appliedFilterObjects.some(appliedFilter =>
                        appliedFilter.value.some(val => filterObj.values.includes(val))
                    );
                });

                return onlyForFilterMatch && !excludeForFiltersMatch;
            })
    }

    function onTableSort(header, direction) {
        setTableSortDir(direction);
        setTableSortBy(header);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    function onSearchTermChange(event) {
        setSearchTerm(event.target.value);
    }

    function onStatusChange(key) {
        setNoDataMessage("No results found, please try a different toggle.");
        setSelectedStatus(key);
    }

    function updateStatuses(passFailCount, requireSignOffCount) {
        statuses.find((status) => status.key === "passfailresult.passed").count = passFailCount;
        statuses.find((status) => status.key === "requiremanagersignoff").count = requireSignOffCount;
    }

    function getSignOffStatusDisplayText(check) {
        if (check.managerSignOff?.signOffDate != null) {
            return <span className="text-intent-success">Signed-off</span>;
        }

        if (!check.requireManagerSignOff) {
            return <span className="text-intent-info">Not required</span>;
        }

                return <span className="text-intent-danger">Pending</span>;
    }

    function getPassFailDisplayText(check) {
        if (check.passFailResult?.passed === false) {return <span className="text-intent-danger">Fail</span>;}
        if (check.passFailResult?.passed === true) {return <span className="text-intent-success">Pass</span>;}

        return <span className="text-intent-info">N/A</span>;
    }

    function redirectToTemplatesList() {
        navigate("/checks/templates");
    }

    function redirectToCheckSettings() {
        navigate("/checks/settings");
    }

    function formatFilters() {
        return [
            ...predefinedFilters.flatMap((filter) => {
                return filter.value.map((value) => {
                    return {
                        key: filter.name,
                        value: value.toString()
                    }
                });
            }),
            ...appliedFilters.flatMap((filter) => {
                return filter.value.map((value) => {
                    return {
                        key: filter.name,
                        value: value.toString()
                    }
                });
            }),
            ...selectedLocations.map((location) => {
                return {
                    key: "location",
                    value: location.toString()
                }
            }),
            ...(selectedStatus !== null
            ? [
                {
                key: selectedStatus,
                value: true.toString(),
                },
            ]
            : [])
        ]
    }

    function getEntitySubject(entity) {
        if (entity.$entityType === 'Asset') {
            return entity.registration;
        } else if (entity.$entityType === 'Location') {
            return entity.name;
        } else {
            return "N/A";
        }
    }

    function searchChecks() {
        if (canAccessPage && isInitalSetupComplete && !isLoadingData && !locationSelectorOpen) {
            currentPageNumber.current = pageNumber;
            currentPageSize.current = pageSize;
            currentTableSortBy.current = tableSortBy;
            currentTableSortDir.current = tableSortDir;
            currentAppliedFilters.current = appliedFilters;
            currentSearchTerm.current = debouncedSearchTerm;
            currentLocations.current = selectedLocations;
            currentStatus.current = selectedStatus;

            setLoadingData(true);
            let filters = formatFilters();
            
            CheckService.getAllChecks(pageNumber, pageSize, debouncedSearchTerm, tableSortBy.sortName, tableSortDir, filters)
                .then(r => {
                    let data = [];

                    if (r !== null) {
                        updateStatuses(r.passFailCount, r.requireSignOffCount);

                        if (r.paging !== null) {
                            setTotalRecords(r.paging.totalCount);

                            if (r.paging.data !== null) {
                                data = r.paging.data.map((check) => {
                                    const row = [check.friendlyId];
                                    var template = templatesFilterOptions.templates.find(t => t.templateName === check.templateName);
                                    
                                    if (filteredTableHeaders.includes("Check name")) {
                                        row.push(<Link key={`link-${check.id}`} to={`/checks/${check.id}`}>{`${check.templateName}`}</Link>);
                                    }
                                    if (filteredTableHeaders.includes("Subject")) {
                                        row.push(getEntitySubject(check.entity) ?? 'N/A');
                                    }
                                    if (filteredTableHeaders.includes("Category")) {
                                        row.push(templateOptions.templateCategories.find(cat => cat.key === template.category).name);
                                    }
                                    if (filteredTableHeaders.includes("Sub-category")) {
                                        row.push(templateOptions.templateCategories.find(cat => cat.key === template.category).subCategories
                                            .find(sc => sc.key === template.subCategory).name);
                                    }
                                    if (filteredTableHeaders.includes("Location")) {
                                        row.push(getEntityLocation(check.entity) ?? 'N/A');
                                    }
                                    if (filteredTableHeaders.includes("Completed by")) {
                                        row.push(check.createUser.fullName);
                                    }
                                    if (filteredTableHeaders.includes("Date Completed")) {
                                        row.push(check.endDateTime ? <DateToLocal key={`enddatetime-${check.checkId}`} format='DD/MM/YYYY'>{check.endDateTime}</DateToLocal> : null);
                                    }
                                    if (filteredTableHeaders.includes("Sign-off Status")) {
                                        row.push(getSignOffStatusDisplayText(check));
                                    }
                                    if (filteredTableHeaders.includes("Check Status")) {
                                        row.push(getPassFailDisplayText(check));
                                    }
                                    if (filteredTableHeaders.includes("Defect Count")) {
                                        row.push(check.defectCount ?? 0);
                                    }
                                    return row;
                                });
                            }
                        }
                    }

                    setTableRows(data);
                    setLoadingData(false);
                    setTimeout(function () {
                        if (inputRef.current !== null) {
                            inputRef.current.focus();
                        }
                    });
                    dispatch(SetRouting(route, route, id));
                },
                function () {
                    setTableRows([]);
                    setTotalRecords(0);
                    setLoadingData(false);
                    NotificationToaster.show(Intent.DANGER, "Could not retrieve checks, please try again");
                }
            );
        }
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">

                    {withBreadcrumbs && breadcrumbs != null && (
                        <Breadcrumbs items={breadcrumbs} />
                    )}

                    <h1>Completed Checks</h1>
                    
                    {(withCheckSettingsButton || withManageTemplatesButton) && (
                        <div className="spacer-bottom">
                            {withCheckSettingsButton && (
                                <UserAccess
                                    perform={templateManagerAction}
                                    yes={() => (
                                        <Fragment>
                                                <Button
                                                    text="Check settings"
                                                    intent="primary"
                                                    onClick={redirectToCheckSettings}
                                                    className="spacer-right"
                                                    disabled={isLoadingData}
                                                />
                                        </Fragment>
                                    )}
                                />
                            )}
                            {withManageTemplatesButton && (
                                <UserAccess
                                    perform={templateBuilderAction}
                                    yes={() => (
                                        <Fragment>
                                                <Button
                                                    text="Manage templates"
                                                    intent="primary"
                                                    onClick={redirectToTemplatesList}
                                                    disabled={isLoadingData}
                                                />
                                        </Fragment>
                                    )}
                                />
                            )}
                        </div>
                    )}

                    <ShowHide evaluator={tableRows.length === 0 && !isLoadingData && debouncedSearchTerm === "" && pageFilters.length === 0}
                        hide={(
                            <Fragment>
                                <div className="filter-controls">
                                    <div className="inline-items">
                                        <div className="inline-item fill">
                                            <div className="form-field-full">
                                                <FormTextInput
                                                    inputRef={inputRef}
                                                    placeholder="Search completed checks"
                                                    onChange={onSearchTermChange}
                                                    value={searchTerm}
                                                    large
                                                    disabled={isLoadingData}
                                                    icon="search"
                                                    id="template-search-field"
                                                />
                                            </div>
                                        </div>
                                        <div className="inline-item">
                                            {withLocationsFilter && (
                                                <FormLocationSelector
                                                    businessArea={"Tacho"}
                                                    loading={isLoadingData}
                                                    selectedLocations={selectedLocations}
                                                    setSelectedLocations={onSelectedLocationsChange}
                                                    useHierarchy={useHierarchy}
                                                    setUseHierarchy={setUseHierarchy}
                                                    useLocationDefaults={false}
                                                    useCustomLocations
                                                    customLocations={locationList}
                                                    locationDefaultsToTrue
                                                    onLocationListOpened={onLocationListOpened}
                                                    onLocationListClose={onLocationListClose}
                                                />
                                            )}
                                        </div>
                                        <div className="inline-item">
                                            <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                        </div>
                                        
                                    </div>                               
                                    <Filter
                                        filterName="checks"
                                        visible={showFilters}
                                        filters={pageFilters}
                                        onUpdate={onFilterChange}
                                        loading={isLoadingData || !isInitalSetupComplete}
                                        onClickOutside={onToggleFilter}
                                    />
                                </div>
                                <div className={classNames("inline-items", "spacer-top-small", {"spacer-bottom-small": !isLoadingData})}>
                                    <FormToggleList
                                        disabled={isLoadingData || !isInitalSetupComplete}
                                        items={statuses}
                                        value={selectedStatus}
                                        onChange={onStatusChange}
                                    />
                                    
                                </div>
                            </Fragment>
                        )}
                    />

                    <ListingTable
                        id="listing-table-checks"
                        headers={filteredTableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        onPagingChange={onPagingChange}
                        loadingData={isLoadingData || !isInitalSetupComplete}
                        noDataMessage={noDataMessage}
                        sortable
                        sortableHeaders={filteredSortableHeaders}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onTableSort}
                    />
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

ChecksListing.propTypes = {
  breadcrumbs: PropTypes.array,
  withBreadcrumbs: PropTypes.bool,
  withCheckSettingsButton: PropTypes.bool,
  withManageTemplatesButton: PropTypes.bool,
  withLocationsFilter: PropTypes.bool,
  entityId: PropTypes.string,
  employeeId: PropTypes.string,
  category: PropTypes.string
};